<template>
  <LoadingDiv v-if="isLoading" />
  <div v-else class="policy-onboard-form">
    <div class="policy-onboard-intro" v-if="newClient?.created?.name">
      <h2>Set up client plans</h2>
      <p>Add plans for {{ newClient.created.name }} before creating member records.</p>
    </div>
    <section v-if="plans.length" class="policy-onboard-section" aria-labelledby="policy-added-heading">
      <div class="policy-onboard-copy"><h3 id="policy-added-heading">Added plans</h3><p>Review the plans selected for this client.</p></div>
      <div class="policy-added-list">
        <article v-for="plan in plans" :key="plan.id" class="policy-added-row">
          <div>
            <h4>{{ plan.name }}</h4>
            <p>{{ cat == 'hmo' ? plan.desc : 'Reference Plan' }}</p>
            <p v-if="cat == 'hmo'">Individual: &#8358;{{ toMoney(plan.premiumIndividual) }} <span>·</span> Family: &#8358;{{ toMoney(plan.premiumFamily) }}</p>
            <p v-else>Individual and family premiums: N/A</p>
          </div>
          <button type="button" class="policy-remove-button" :disabled="isSubmitting" :aria-label="`Remove ${plan.name}`" @click="handleRemove(plan.id)"><i class="material-icons" aria-hidden="true">close</i></button>
        </article>
      </div>
    </section>
    <section class="policy-onboard-section" aria-labelledby="policy-type-heading">
      <div class="policy-onboard-copy"><h3 id="policy-type-heading">Policy type</h3><p>Select a plan to attach to this client.</p></div>
      <div class="policy-onboard-field">
        <label for="filterPlan">Plan</label>
        <div class="policy-onboard-control">
          <FilterSimpleDiv inputId="filterPlan" fieldName="Plan" :hideLabel="true" inputPlaceholder="Select policy"
            :clear="resetPlan" :data="filterPlanData" :search="false" :browserAutocomplete="cat == 'tpa' ? 'off' : undefined"
            @selected="handlePick" @clear="handleClear" />
        </div>
      </div>
    </section>
    <template v-if="selectedPlan">
      <section class="policy-onboard-section" v-if="cat != 'tpa'" aria-labelledby="policy-premiums-heading">
        <div class="policy-onboard-copy"><h3 id="policy-premiums-heading">Premiums</h3><p>Confirm the individual and family premiums for this plan.</p></div>
        <div class="policy-onboard-fields">
          <div class="policy-onboard-field" :class="{ 'has-error': error && !premiumIndividual }">
            <label for="premiumIndividual">Individual premium (&#8358;)</label>
            <input type="number" id="premiumIndividual" v-model="premiumIndividual" class="validate" required />
          </div>
          <div class="policy-onboard-field" :class="{ 'has-error': error && !premiumFamily }">
            <label for="premiumFamily">Family premium (&#8358;)</label>
            <input type="number" id="premiumFamily" v-model="premiumFamily" class="validate" required />
          </div>
        </div>
      </section>
      <section class="policy-onboard-section" v-if="premiumIndividual != checkIndividual || premiumFamily != checkFamily || cat == 'tpa'" aria-labelledby="policy-benefits-heading">
        <div class="policy-onboard-copy"><h3 id="policy-benefits-heading">Custom benefits</h3><p>Upload benefits for {{ selectedPlan.name }}.</p></div>
        <div class="policy-onboard-field">
          <label for="policy-benefits-file">Benefits file</label>
          <input id="policy-benefits-file" type="file" @change="handleFile" />
          <p v-if="fileError" class="policy-file-error" role="alert">{{ fileError }}</p>
        </div>
      </section>
    </template>
    <div class="policy-onboard-actions">
      <button type="button" class="btn policy-skip-button modal-trigger" data-target="confirmSkipPlans" :disabled="isSubmitting" v-if="newClient?.created?.id">Skip for now</button>
      <div class="policy-onboard-action-group">
        <button type="button" class="btn policy-add-button" :disabled="isSubmitting || !selectedPlan" :class="{ disabled: isSubmitting || !selectedPlan }" @click="handleAdd">
          <i class="material-icons" aria-hidden="true">add</i>{{ isSubmitting && selectedPlan ? `Adding (${uploadProgress})` : 'Add plan' }}
        </button>
        <button type="button" class="btn" :disabled="isSubmitting || !plans.length" :class="{ disabled: isSubmitting || !plans.length }" @click="handleNext">
          <span>{{ isSubmitting && !selectedPlan ? 'Loading...' : 'Load plans' }}</span><i class="material-icons" aria-hidden="true">arrow_forward</i>
        </button>
      </div>
    </div>
    <Modal id="confirmSkipPlans" class="modal" title="Skip plan setup?"
      message="Plans are required before members can be created. You can add them later in the client portal." @confirm="handleSkipConfirm" />
  </div>
</template>
<script>
import { ref, onMounted, computed, watchEffect } from "vue";
import { changeToMoney } from "@/composables/fxLibrary/coreFx";
import useStorage from "@/composables/useStorage";
import postItem from "@/composables/postItem";
import getItems from "@/composables/getItems";
import LoadingDiv from "@/components/LoadingDiv.vue";
import FilterSimpleDiv from "@/components/search/FilterSimpleDiv.vue";
import Modal from "@/components/alerts/ModalComponent.vue";
import { adminAPI } from '@/laravel/config';
import { useRouter } from "vue-router";

export default {
  name: "PolicyOnboardComponent",
  components: { LoadingDiv, FilterSimpleDiv, Modal },
  props: ["cat", "newClient"],
  emits: ["next"],
  setup(props, context) {
    const file = ref(null);
    const inputFile = ref(null);
    const fileError = ref(null);
    const isLoading = ref(true);
    const isSubmitting = ref(false);
    const pulse = ref(false);
    const error = ref(null);
    const responsePlanDefault = ref(null);
    const originalPlanDefault = ref(null);
    const selectedItem = ref(null);
    const selectedPlan = ref(null);
    const resetPlan = ref(false);
    const next = ref(false);
    const policy = ref([]);
    const custom = ref(false);
    const premiumIndividual = ref(null);
    const premiumFamily = ref(null);
    const checkIndividual = ref(null);
    const checkFamily = ref(null);
    const router = useRouter();
    const types = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    const isPreCreateTpa = computed(() => props.cat == "tpa" && !props.newClient?.created?.id);

    // storage
    const { uploadFile, uploadProgress, uploadError, data, completed } =
      useStorage();

    // toMoney
    const toMoney = (val) => {
      const { fx } = changeToMoney(val);
      return fx.value;
    };

    // Plan
    const filterPlanData = computed(() => {
      // start
      let val = {
        "Plans Loading...": null,
      };

      let valPlus;

      if (responsePlanDefault.value) {
        if (responsePlanDefault.value.key == "filterPlan") {
          valPlus = responsePlanDefault.value.value.reduce((newArr, x) => {
            newArr[x.name] = null;
            return newArr;
          }, {});
          val = { ...valPlus };
        }
      }

      // return final
      return val;
    });

    // starter
    onMounted(() => {
      // get started
      getPlans();
    });

    const getPlans = async () => {
      // get policies
      getSearchItemsFromApi("plan/private", "filterPlan");
    };

    // search function
    const getSearchItemsFromApi = (table, key) => {
      (async () => {
        const { res } = await getItems(table);
        if (res.value) {
          // search
          responsePlanDefault.value = { key: key, value: res.value.data };
          // original
          originalPlanDefault.value = { key: key, value: res.value.data };
        }
        // end
        isLoading.value = false;
      })();
    };

    // handle file
    const handleFile = (e) => {
      const selected = e.target.files[0];
      if (selected && types.includes(selected.type)) {
        file.value = selected;
        inputFile.value = file.value.name;
        fileError.value = null;
      } else {
        file.value = null;
        inputFile.value = null;
        fileError.value = "Please select an Excel file (.XLSX, XLS)";
      }
    };

    // handle add
    const handleReset = () => {
      // clear files
      file.value = null; //clear file value
      inputFile.value = null; // Clear the file input value
      uploadProgress.value = "0%";
      uploadError.value = null;
      data.value = null;
      completed.value = null;
      // null all
      selectedItem.value = null;
      selectedPlan.value = null;
      custom.value = false;
      premiumIndividual.value = null;
      premiumFamily.value = null;
      checkIndividual.value = null;
      checkFamily.value = null;
      // clear component
      resetPlan.value = true;
      // clear error
      error.value = false;
      // clear loading
      isLoading.value = false;
      // clear submitting
      isSubmitting.value = false;
      // clear pulse
      pulse.value = false;
    };

    // handle update
    const handUpdate = () => {
      // check for duplicates
      let duplicate = false;

      policy.value.forEach((x) => {
        if (x.plan_id == selectedPlan.value.plan_id) {
          duplicate = true;
        }
      });

      // if no duplicate
      if (!duplicate) {
        // review description
        if (
          premiumIndividual.value != checkIndividual.value ||
          premiumFamily.value != checkFamily.value
        ) {
          selectedPlan.value.desc = "Customized Plan";
        }
        // load
        policy.value.push({
          ...selectedPlan.value,
          premiumIndividual: premiumIndividual.value,
          premiumFamily: premiumFamily.value,
        });
      }
    };

    // handle add
    const handleAdd = async () => {
      if (
        selectedPlan.value &&
        (premiumIndividual.value || props.cat == "tpa") // premiumFamily no longer necessary
      ) {
        
        // Start Uploading
        isSubmitting.value = true;

        // zero premiums for tpa cat
        if (props.cat == "tpa") {
          premiumIndividual.value = 0;
          premiumFamily.value = 0;
        }

        // initiate for update
        let plan = null;
        let individual = null;
        let family = null;
        let scope = props.newClient?.created?.id; // get client id from props

        // new plan
        plan = {
          id: selectedPlan.value.plan_id, // 1 for test
        };

        // update individual premium
        if (checkIndividual.value != premiumIndividual.value) {
          individual = {
            plan_id: null, // update this on server
            amount: premiumIndividual.value,
            scope: scope,
            type: "i",
          };
        }

        // update family premium
        if (checkFamily.value != premiumFamily.value) {
          family = {
            plan_id: null, // update this on server
            amount: premiumFamily.value,
            scope: scope,
            type: "f",
          };
        }

        if (isPreCreateTpa.value) {
          // TPA add creates the client after policy/status/settings are collected.
          handUpdate();
          handleReset();
        } else if (file.value) {
          // File (Benefits)
          // Start Uploading
          uploadError.value = null;

          // Cloud Function
          const fx = "planLoader";

          // round up
          const cloudInput = {
            plan: plan,
            individual: individual,
            family: family,
            scope: scope,
            action: "update",
            endPoint: `${adminAPI}/plan/benefits/bulk`,
          };

          // upload
          uploadFile(file.value, fx, cloudInput);
        } else {
          // update without File
          // (without File)

          // Start Uploading
          uploadProgress.value = "100%";

          // round up
          const newVal = {
            plan: plan,
            individual: individual,
            family: family,
            scope: scope,
            action: "update",
            benefits: [], // empty array (no Benefit change required)
          };

          // post item
          const { res, err } = await postItem("plan/benefits/bulk", newVal);

          if (res.value) {
            // success
            handUpdate();
            // restart
            handleReset();
          } else {
            error.value = err.value;
            isSubmitting.value = false;
          }
        }
      } else {
        error.value = true;
      }
    };

    // watch upload
    watchEffect(() => {
      if (completed.value) {
        console.log(data.value);
        // success
        handUpdate();
        // restart
        handleReset();
      }
      if (uploadError.value) {
        // restart
        // handleReset();
        // stop
        error.value = uploadError.value;
        isSubmitting.value = false;
      }
    });

    // clear (null reset)
    const handleClear = (val) => {
      if (val.id == "filterPlan") {
        resetPlan.value = val.value;
      }
    };

    // handle remove
    const handleRemove = async (val) => {
      // filter delete
      let newPolicy = policy.value.filter((x) => x.id != val);

      // update
      policy.value = newPolicy;

      // clean up
      handleReset();
    };

    // plan display filter watcher
    watchEffect(() => {
      if (responsePlanDefault.value && originalPlanDefault.value) {
        // start update list
        const filteredPlans = originalPlanDefault.value.value.filter(
          (plan) => !policy.value.some((active) => active.plan_id === plan.id)
        );

        // complete update list
        responsePlanDefault.value.value = filteredPlans;
      }
    });

    // handle submit
    const handleNext = async () => {
      if (!policy.value.length) {
        error.value = "No Policy Added!"; // most unlikely
      } else {

      //check if plan is yet to be submitted
      if (selectedPlan.value) {
        // notify submit
        pulse.value = true;
      }else{
        // Good to go!

        // start
        isSubmitting.value = true;

        // initiate
        let client = props.newClient?.created?.id; // get client id from props
        let plans = [];

        // load
        policy.value.forEach((x) => {
          plans.push(x.plan_id);
        });

        if (isPreCreateTpa.value) {
          context.emit("next", {
            id: "policy",
            value: plans,
          });
          return;
        }

        // add selected plans to client
        const newVal = {
          client: client,
          plans: plans,
        };

        // post item
        const { res, err } = await postItem("plan/client/plans", newVal);

        if (res.value) {
          // success
          // emit new parameters
          context.emit("next", {
            id: "policy",
            value: true,
          });
        } else {
          error.value = err.value;
          isSubmitting.value = false;
        }
        
      }
        
      }
    };

    // handle skip
    const handleSkipConfirm = (val) => {
      if (val && props.newClient?.created?.id) {
        router.push({
          name: props.cat == "tpa" ? "tpaView" : "privateView",
          params: { id: props.newClient.created.id },
        });
      }
    };

    // get option
    const handlePick = (val) => {
      selectedItem.value = val;

      // chek for reset condition
      if (selectedItem.value.id == "filterPlan" && val.value == null) {
        // clear every here
        handleReset();
      }

      // watchEffect will take over from here
    };

    // search watcher
    watchEffect(() => {
      // start
      if (selectedItem.value) {
        // sort
        if (selectedItem.value.id == "filterPlan") {
          //set
          if (!selectedItem.value.value) {
            selectedPlan.value = null;
          } else {
            responsePlanDefault.value.value.forEach((x) => {
              if (x.name == selectedItem.value.value) {

                // reverse premium
                x.premiums.reverse();

                // found
                premiumIndividual.value = x.premiums.find(x => x.type == "i" && x.scope == 0).amount;
                premiumFamily.value = x.premiums.find(x => x.type == "f" && x.scope == 0).amount;
                checkIndividual.value = x.premiums.find(x => x.type == "i" && x.scope == 0).amount;
                checkFamily.value = x.premiums.find(x => x.type == "f" && x.scope == 0).amount;

                // load
                selectedPlan.value = {
                  id: Date.now(),
                  star: x.id,
                  plan_id: x.id,
                  scheme_id: x.scheme_id,
                  name: x.name,
                  desc: x.description,
                };
                // clear
                custom.value = false;
              }
            });
          }
        }
      }
    });

    // output results (plans)
    const plans = computed(() => {
      if (!policy.value.length) {
        return [];
      } else {
        return policy.value;
      }
    });

    return {
      isLoading,
      isSubmitting,
      pulse,
      error,
      handleNext,
      handleSkipConfirm,
      handleAdd,
      handleRemove,
      handleClear,
      handlePick,
      handleFile,
      uploadProgress,
      fileError,
      uploadError,
      file,
      inputFile,
      toMoney,
      selectedItem,
      filterPlanData,
      next,
      selectedPlan,
      resetPlan,
      custom,
      premiumIndividual,
      premiumFamily,
      checkIndividual,
      checkFamily,
      plans,
    };
  },
};
</script>
    
<style>
.policy-onboard-form { max-width: 70rem; width: 100%; }
.policy-onboard-intro { margin-bottom: 1.5rem; }
.policy-onboard-intro h2 { color: var(--ui-foreground); font-size: 1.125rem; font-weight: 600; margin: 0 0 .5rem; }
.policy-onboard-intro p, .policy-onboard-copy p { color: var(--ui-muted-foreground); font-size: .8125rem; line-height: 1.6; margin: 0; }
.policy-onboard-section { align-items: start; border-bottom: 1px solid var(--ui-border); display: grid; gap: 2.5rem; grid-template-columns: 13rem minmax(0, 1fr); padding: 1.75rem 0; }
.policy-onboard-copy h3 { color: var(--ui-foreground); font-size: 1rem; font-weight: 600; line-height: 1.5; margin: 0 0 .35rem; }
.policy-onboard-fields { display: grid; gap: 1.25rem; grid-template-columns: repeat(2, minmax(0, 1fr)); min-width: 0; }
.policy-onboard-field { min-width: 0; }
.policy-onboard-field > label { color: var(--ui-foreground); display: block; font-size: .8125rem; font-weight: 500; line-height: 1.5; margin: 0 0 .5rem; }
.policy-onboard-field input { box-sizing: border-box; display: block; margin: 0; min-height: 2.75rem; width: 100%; }
.policy-onboard-control { position: relative; }
.policy-onboard-field.has-error input { border-color: var(--ui-destructive); }
.policy-onboard-field .policy-file-error { color: var(--ui-destructive); font-size: .75rem; margin-top: .5rem; }
.policy-added-list { min-width: 0; }
.policy-added-row { align-items: start; border-bottom: 1px solid var(--ui-border); display: flex; gap: 1rem; justify-content: space-between; padding: .875rem 0; }
.policy-added-row:first-child { padding-top: 0; }
.policy-added-row h4 { color: var(--ui-foreground); font-size: .875rem; font-weight: 600; margin: 0 0 .35rem; }
.policy-added-row p { color: var(--ui-muted-foreground); font-size: .8125rem; line-height: 1.6; margin: .25rem 0; overflow-wrap: anywhere; }
.policy-added-row p > span { padding: 0 .5rem; }
.policy-remove-button { align-items: center; background: transparent; border: 1px solid var(--ui-border); border-radius: calc(var(--ui-radius) - 3px); color: var(--ui-muted-foreground); cursor: pointer; display: inline-flex; flex-shrink: 0; height: 2rem; justify-content: center; width: 2rem; }
.policy-remove-button i { font-size: 1rem; }
.policy-onboard-actions { align-items: center; display: flex; flex-wrap: wrap; gap: .75rem; justify-content: space-between; padding-top: 1.25rem; }
.policy-onboard-action-group { display: flex; flex-wrap: wrap; gap: .75rem; margin-left: auto; }
.policy-onboard-actions .btn { font-size: .8125rem; font-weight: 600; gap: .5rem; }
.policy-onboard-actions i { font-size: 1rem; }
.policy-onboard-actions :is(.policy-skip-button, .policy-add-button) { background: var(--ui-background); border: 1px solid var(--ui-border); color: var(--ui-foreground); }
@media (max-width: 900px) { .policy-onboard-section { gap: 1rem; grid-template-columns: minmax(0, 1fr); } }
@media (max-width: 600px) { .policy-onboard-fields { grid-template-columns: minmax(0, 1fr); } }
</style>
